import {
  Anchor,
  Box,
  Checkbox,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core"
import {
  MagnifyingGlassIcon,
} from "@phosphor-icons/react"
import { useTranslation } from "react-i18next"
import { WizardSection } from "./WizardSection"
import classes from "./wizard.module.css"
import { ChannelBadge, PriorityBadge } from "./wizard-badges"
import type { DnFilters, DnRow } from "./types"
import { renderBusinessName } from "../../../utils/render-business-name"

interface Props {
  rows         : DnRow[]
  loading      : boolean
  selectedIds  : string[]
  onToggle     : (id: string, next: boolean) => void
  filters      : DnFilters
  onFiltersChange: (next: DnFilters) => void
  zoneCounts   : { current: number; total: number }
}

interface FilterChipProps {
  active : boolean
  label  : string
  onClick: () => void
}

/** Canvas filter pill: round, chosen = dark fill. */
function FilterChip({ active, label, onClick }: FilterChipProps) {
  return (
    <UnstyledButton className={classes.pill} data-active={active || undefined} aria-pressed={active} onClick={onClick}>
      {label}
    </UnstyledButton>
  )
}

export function DnSelectionSection({
  rows,
  loading,
  selectedIds,
  onToggle,
  filters,
  onFiltersChange,
  zoneCounts,
}: Props) {
  const { t } = useTranslation(["delivery"])
  const selectedCount = selectedIds.length

  return (
    <WizardSection n={2} title={t("delivery:route.wizardUi.select.title")} aside={selectedCount > 0 ? <Text span className={classes.status}>{t("delivery:route.wizardUi.select.selected", { count: selectedCount })}</Text> : null}>
        <Stack gap="md">
          {/* Filter toolbar */}
          <Group gap="xs" wrap="wrap">
              <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)" fw={500}>{t("delivery:route.wizardUi.select.filter")}</Text>
              <FilterChip
                active={!!filters.zone}
                label={filters.zone ? `${filters.zone} (${zoneCounts.current})` : t("delivery:route.wizardUi.select.allZones", { count: zoneCounts.total })}
                onClick={() => onFiltersChange({ ...filters, zone: null })}
              />
              <FilterChip
                active={filters.vipOnly}
                label={t("delivery:route.wizardUi.select.vip")}
                onClick={() => onFiltersChange({ ...filters, vipOnly: !filters.vipOnly })}
              />
              <FilterChip
                active={filters.channel === "LOCAL_DELIVERY"}
                label={t("delivery:route.wizardUi.select.localOnly")}
                onClick={() => onFiltersChange({ ...filters, channel: filters.channel === "LOCAL_DELIVERY" ? null : "LOCAL_DELIVERY" })}
              />
              <FilterChip
                active={filters.slaRisk}
                label={t("delivery:route.wizardUi.select.slaRisk")}
                onClick={() => onFiltersChange({ ...filters, slaRisk: !filters.slaRisk })}
              />
              <Box style={{ flex: 1 }} />
              <TextInput
                size="sm"
                placeholder={t("delivery:route.wizardUi.select.search")}
                leftSection={<MagnifyingGlassIcon size={16} />}
                value={filters.search}
                onChange={(e) => onFiltersChange({ ...filters, search: e.currentTarget.value })}
                w={240}
              />
          </Group>

          {/* DN list */}
          <Paper withBorder radius="sm">
            {loading ? (
              <Group justify="center" p="lg">
                <Loader size="sm" />
              </Group>
            ) : rows.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py="lg">
                {t("delivery:route.wizardUi.select.empty")}
              </Text>
            ) : (
              <ScrollArea.Autosize mah={360}>
                <Stack gap={0}>
                  {rows.map((dn) => {
                    const id           = dn.id ?? ""
                    const selected     = selectedIds.includes(id)
                    const customerName =
                      renderBusinessName(dn.receiverBusiness, "") ||
                      renderBusinessName(dn.receiverBusiness?.partner, "-")
                    const channel      = dn.classification?.deliveryChannel
                    const priority     = dn.sla?.priority
                    return (
                      <Group
                        key={id}
                        gap="sm"
                        wrap="nowrap"
                        px="md"
                        py="sm"
                        style={{
                          borderBottom: "1px solid var(--mantine-color-gray-1)",
                          background  : selected ? "var(--mantine-color-brand-0)" : undefined,
                                                    cursor      : "pointer",
                        }}
                        onClick={() => onToggle(id, !selected)}
                      >
                        <Checkbox
                          checked={selected}
                          onChange={(e) => onToggle(id, e.currentTarget.checked)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Group gap={6} wrap="nowrap">
                            <Text size="sm" fw={600} truncate>{customerName}</Text>
                            <PriorityBadge priority={priority} />
                          </Group>
                          <Text size="sm" c="dimmed" mt={2} truncate>
                            {dn.refCode || id.slice(0, 8)}
                            {dn.deliveryDate && ` · ${dn.deliveryDate.slice(0, 10)}`}
                          </Text>
                        </Box>
                        <Anchor size="sm" fw={600} c="red" onClick={(e) => e.stopPropagation()}>
                          {dn.refCode || id.slice(0, 8)}
                        </Anchor>
                        <ChannelBadge channel={channel} />
                        <Text size="sm" c="dimmed" ff="monospace">
                          {dn.totalItemCount ? t("delivery:route.wizardUi.pieces", { count: dn.totalItemCount }) : "-"}
                        </Text>
                      </Group>
                    )
                  })}
                </Stack>
              </ScrollArea.Autosize>
            )}
          </Paper>
        </Stack>
    </WizardSection>
  )
}
